/**
 * A Listen & Write passage as the client walks it. Which pages make a
 * passage and which sentences are dictated are the server's
 * (`StoryPage.listenPassage`, listen & write design §10.8); nothing here
 * decides either, it only addresses what the server named.
 */

/** `tutor_core::listening::MIN_RETELLING_WORDS`; the server refuses fewer. */
export const MIN_WORDS = 15;
/** `tutor_core::listening::MAX_RETELLING_CHARS`, counted as the server counts: characters. */
export const MAX_CHARS = 2_000;

export type PassagePage = { readonly id: string; readonly number: number; readonly sentences: readonly string[] };
export type SentenceRef = { readonly page: number; readonly sentence: number };
/** One sentence as `speak` addresses it: a page id and an index into that page's `sentences` (§10.1). */
export type PassageSentence = { pageId: string; page: number; sentence: number; text: string };

export function sentencesOf(pages: readonly PassagePage[]): PassageSentence[] {
  return pages.flatMap((p) => p.sentences.map((text, sentence) => ({ pageId: p.id, page: p.number, sentence, text })));
}

/** The server's dictation refs, resolved to their text; a ref this passage lacks is dropped. */
export function dictated(pages: readonly PassagePage[], refs: readonly SentenceRef[]): PassageSentence[] {
  const all = sentencesOf(pages);
  return refs.flatMap((r) => all.filter((s) => s.page === r.page && s.sentence === r.sentence));
}

/** §4: a clip is cached per (page, sentence, slow) for the activity. */
export function clipKey(s: { pageId: string; sentence: number }, slow: boolean): string {
  return `${s.pageId}:${s.sentence}:${slow ? 'slow' : 'normal'}`;
}

/** The server's `split_whitespace` count, so the button opens exactly when the server would accept. */
export function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

export function canSubmit(text: string): boolean {
  const trimmed = text.trim();
  return wordCount(trimmed) >= MIN_WORDS && Array.from(trimmed).length <= MAX_CHARS;
}

/**
 * §3.4's soft guide, by the learner's level: shown as a range and never as a
 * counter, because a word count is not a goal (§1, non-goals). Anything below
 * B2 gets B1's range; the books start at B1, and a lower learner reading one
 * is better served by the shorter guide.
 */
export function guideRange(level: string): { min: number; max: number } {
  return level === 'B2' || level === 'C1' || level === 'C2' ? { min: 60, max: 200 } : { min: 40, max: 150 };
}
